<template>
  <div>
    <div id="billing-dataview" class="page-table scrollable">
      <div class="flex align-center mt-5 mb-10">
        <el-input
          placeholder="Search name"
          v-model="accountSearch"
          class="mr-10"
          style="max-width: 193px;"
          clearable
        />
        <el-select
          v-model="monthSelection"
          placeholder="Select Billing Month"
          size="large"
          filterable
        >
          <el-option
            v-for="(month, index) in months"
            :key="index"
            :label="month.label"
            :value="month.value"
          />
        </el-select>
        <el-button class="mb-0" @click="doExport" style="margin-left: auto;">
          Export
        </el-button>
        <el-button @click="doExportCDR" v-if="hasCDRExport">Export CDR</el-button>
      </div>

      <div class="billing-table card-base card-shadow--medium">
        <vxe-grid
          round
          class="user-stats-table"
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 6, oSize: 0 }"
          :loading="loading || tableLoading"
          :columns="[this.expandColumn, ...this.tableColumns]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ExportToCsv } from "export-to-csv";
import moment from "moment-timezone";
import { debounce } from "lodash";

export default {
  name: "BillingRecords",
  props: {
    records: { required: true },
    loading: { required: true }
  },
  components: {},
  data() {
    return {
      hasCDRExport: false,
      cdrUrl: null,
      exportCSV: false,
      accountSearch: "",
      total: 0,
      billingsChannel: null,
      tableData: [],
      allData: [],
      visibleActionsId: "",
      tableLoading: false,
      monthSelection: null,
      startDate: null,
      endDate: null,
      months: [],
      expandColumn: {
        type: "expand",
        width: "25px",
        showOverflow: true,
        slots: {
          content: ({ row }) => {
            return [
              <div class="billing-lineitem-table">
                <vxe-table
                  border="none"
                  show-overflow
                  highlight-hover-row
                  show-header={true}
                  height="300"
                  empty-text="No Davi"
                  scroll-y={{ enabled: true, gt: 10, oSize: 0 }}
                  data={row.lineItems}
                >
                  <vxe-table-column field="qty" title="QTY" width="100" />
                  <vxe-table-column
                    field="price"
                    title="Price"
                    width="150"
                    formatter={this.tablePriceFormatter}
                  />
                  <vxe-table-column
                    field="product"
                    title="Product"
                    width="350"
                  />
                  <vxe-table-column
                    field="description"
                    title="Description"
                    min-width="100"
                  />
                </vxe-table>
              </div>
            ];
          }
        }
      },
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="list-numbers" />
                </div>
              ];
            }
          }
        },
        {
          field: "name",
          title: "Account",
          sortable: true,
          minWidth: 250,
          showOverflow: true
        },
        {
          field: "total",
          title: "Total",
          sortable: true,
          minWidth: 150,
          showOverflow: true,
          sortType: "number",
          slots: {
            default: ({ row }) => {
              return [<span>{this.priceFormatter(row.total)}</span>];
            }
          }
        },
        {
          field: "extensions",
          title: "Extensions",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "faxes",
          title: "Faxes",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "sipTrunks",
          title: "Sip Trunks",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        },
        {
          field: "tollFree",
          title: "Toll Free Mins",
          sortable: true,
          minWidth: 150,
          showOverflow: false
        }
      ]
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    records() {
      if (this.records) {
        this.accountSearch = "";
        this.setMonths();
        this.tableData = [];
        this.total = this.tableData.length;
      }
    },

    monthSelection(val) {
      this.onMonthSelection();
      this.total = this.tableData.length;
    },

    accountSearch: debounce(function() {
      this.filterAccounts();
    }, 300)
  },

  methods: {

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },

    doExportCDR() {
      this.downloadCSV({url: this.cdrUrl, downloadName: `cdr_${this.monthSelection}`});
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} accounts?`, {
        type: "warning"
      })
        .then(() => {
          //export from browser
          let csvData = [];
          for (let i = 0; i < this.tableData.length; i++) {
            const keys = [
              "name",
              "total",
              "extensions",
              "faxes",
              "sipTrunks",
              "tollFree"
            ];

            const csvObject = {};
            const lineItems = this.tableData[i].lineItems;

            for (let key of keys) {
              csvObject[key] =
                this.tableData[i][key] === null ? 0 : this.tableData[i][key];
            }

            csvData.push(csvObject);

            //key map for line items
            //this fakes the root headers to the csv can build properly.
            const lineItemMap = {
              name: "qty",
              total: "price",
              extensions: "product",
              faxes: "description"
            };

            if (lineItems) {
              //first push on the new headers for line items
              csvData.push({
                name: "Quantity",
                total: "Price",
                extensions: "Product",
                faxes: "Description",
                sipTrunks: "",
                tollFree: ""
              });

              for (let i = 0; i < lineItems.length; i++) {
                let csvObjectDist = {};
                for (let key of keys) {
                  csvObjectDist[key] =
                    lineItems[i][lineItemMap[key]] === null
                      ? 0
                      : lineItems[i][lineItemMap[key]];
                }
                csvObjectDist = {
                  ...csvObjectDist,
                  sipTrunks: "",
                  tollFree: ""
                };
                if (!csvObjectDist.faxes) csvObjectDist.faxes = "";
                csvData.push(csvObjectDist);
              }
            }
          }

          const options = {
            filename: "billing_summary",
            showLabels: true,
            headers: [
              "Name",
              "Total",
              "Extensions",
              "Faxes",
              "SIP Trunks",
              "Toll Free Minutes"
            ]
          };

          const csvExporter = new ExportToCsv(options);
          csvExporter.generateCsv(csvData);
        })
        .catch(() => true);
    },
    //General Form and Table Operations
    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },
    onMonthSelection() {
      this.tableLoading = true;
      // reverse records
      const newRecords = this.records.slice().reverse();
      const billingRecords = newRecords[this.monthSelection].resellerData;
      const billingData = newRecords[this.monthSelection].data;

      //check if there is a billing cdr file available for selected month
      if(billingData) {
        this.hasCDRExport = !!billingData.cdrUrl;
        this.cdrUrl = billingData.cdrUrl;
      }

      if (billingRecords) {
        this.tableData = Object.values(billingRecords);
        this.allData = Object.values(billingRecords);
      }
      this.tableLoading = false;
      this.accountSearch = "";
    },
    setMonths() {
      const lastIndex = this.records.length - 1;
      this.months = this.records.map((item, index) => ({
        label: moment(item.date).format("MMMM - YYYY"),
        value: lastIndex - index,
        exactValue: item.date
      }));
      if (this.months.length) {
        this.monthSelection = this.months[0].value;
      }
    },
    filterAccounts() {
      this.tableData = this.accountSearch
        ? this.allData.filter(item =>
            item.name.toLowerCase().includes(this.accountSearch.toLowerCase())
          )
        : (this.tableData = [...this.allData]);
    },

    tablePriceFormatter(data) {
      return this.priceFormatter(data.cellValue);
    },

    priceFormatter(val) {
      return `$${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2
      }).format(val)}`;
    }
  },

};
</script>

<style lang="scss" scoped>
.billing-table {
  height: 450px;
}
</style>
